/**
 * apps/web/src/pages/AuditsPage.tsx
 * -------------------------------------------------------
 * Purpose:
 *   Searchable archive of all audits for the active org.
 *
 * High-level data flow:
 *   - loader (router.tsx -> auditsLoader) calls `/api/audits?search=...`.
 *   - The page renders results and keeps the search query in the URL.
 */

import React, { useMemo, useState } from 'react';
import {
  Card,
  Group,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { Search } from 'lucide-react';
import { Link, useLoaderData, useNavigate } from 'react-router-dom';

import type { AuditSummary } from '@tinypro-audit/contracts';
import {
  AuditReviewAnswerBadge,
  AuditStatusCell,
} from '../components/AuditResolutionBadge';
import { ProjectsNavigation } from '../components/ProjectsNavigation';

/** Loader data returned by auditsLoader(). */
type LoaderData = {
  q: string;
  items: AuditSummary[];
};

export function AuditsPage(): React.ReactElement {
  /** Data provided by the route loader. */
  const { q, items } = useLoaderData() as LoaderData;

  /** Local input state (we only navigate when user presses Enter). */
  const [input, setInput] = useState(q);

  /** Navigate helper for updating the URL search param. */
  const navigate = useNavigate();

  /** Stable display list (could be extended to client-side sorting). */
  const rows = useMemo(() => items, [items]);

  return (
    <Stack>
      <ProjectsNavigation view="pre-site" />
      <Card withBorder radius="md">
        <Group justify="space-between" mb="md">
          <div>
            <Title order={3}>Pre-site form reviews</Title>
            <Text c="dimmed">
              Original pre-site reports and grades. Search by job ID, technician and
              location.
            </Text>
          </div>
        </Group>

        <TextInput
          leftSection={<Search size={16} />}
          placeholder="Search by job #, technician, or location..."
          value={input}
          onChange={(e) => setInput(e.currentTarget.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              const next = input.trim();
              navigate(
                next
                  ? `/projects/pre-site?q=${encodeURIComponent(next)}`
                  : '/projects/pre-site',
              );
            }
          }}
          mb="md"
        />

        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Status</Table.Th>
              <Table.Th>Job</Table.Th>
              <Table.Th>Technician</Table.Th>
              <Table.Th>Location</Table.Th>
              <Table.Th>Caught issue</Table.Th>
              <Table.Th>Install issue awareness</Table.Th>
              <Table.Th>Corrective action</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.map((c) => (
              <Table.Tr key={c.id}>
                <Table.Td>
                  <AuditStatusCell
                    status={c.status}
                    reviewState={c.reviewState}
                  />
                </Table.Td>
                <Table.Td>
                  <Text component={Link} to={`/audits/${c.id}`} fw={600}>
                    {c.jobId}
                  </Text>
                </Table.Td>
                <Table.Td>{c.technicianName}</Table.Td>
                <Table.Td>{c.location}</Table.Td>
                <Table.Td>
                  <AuditReviewAnswerBadge
                    value={c.reviewState.answers.caughtJobsiteAlertIssue}
                  />
                </Table.Td>
                <Table.Td>
                  <AuditReviewAnswerBadge
                    value={
                      c.reviewState.answers.awareOfPotentialInstallationIssue
                    }
                  />
                </Table.Td>
                <Table.Td>
                  <AuditReviewAnswerBadge
                    value={c.reviewState.answers.tookCorrectiveAction}
                  />
                </Table.Td>
              </Table.Tr>
            ))}

            {rows.length === 0 ? (
              <Table.Tr>
                <Table.Td colSpan={7}>
                  <Text c="dimmed">No audits found.</Text>
                </Table.Td>
              </Table.Tr>
            ) : null}
          </Table.Tbody>
        </Table>
      </Card>
    </Stack>
  );
}
